import React, {useEffect, useState} from "react";
import {makeStyles} from "@mui/styles";
import {DataGrid} from '@mui/x-data-grid';
import axios from "axios";
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import {NavLink} from "react-router-dom";
import {API_ENDPOINT, COLOR_BRAND} from "../consts";

const useStyles = makeStyles(() => ({
    container: {flex: 1},
    innerContainer: {display: "flex", justifyContent: "center", width: "100%"},
    box: {width: "100%", maxWidth: 1400, padding: 20},
    toolbar: {display: "flex", alignItems: "center", gap: 16, marginBottom: 20},
    datagrid: {backgroundColor: "#fff"},
    payload: {whiteSpace: "normal", lineHeight: 1.35, padding: "6px 0"},
}));

// Human-readable colour + label per report type.
const TYPE_LABELS = {
    name: {label: "Name", color: "primary"},
    photo: {label: "Photo", color: "warning"},
    ingredients: {label: "Ingredients", color: "secondary"},
};

const formatDate = (seconds) => {
    if (!seconds) return "";
    return new Date(seconds * 1000).toLocaleString();
};

// Render the proposed correction stored in the report payload, per report type.
const renderPayload = (row) => {
    const payload = row.payload || {};
    if (row.type === "name") {
        return (
            <div>
                <div><strong>New:</strong> {payload.proposed_name || "—"}</div>
                {payload.original_name ? <div style={{color: "#888"}}><strong>Was:</strong> {payload.original_name}</div> : null}
            </div>
        );
    }
    if (row.type === "photo") {
        return <div>{payload.note === "user_wont_retake" ? "User can't retake the photo" : (payload.note || "Bad photo reported")}</div>;
    }
    if (row.type === "ingredients") {
        const ingredients = Array.isArray(payload.ingredients) ? payload.ingredients : [];
        const confirmed = ingredients.filter((i) => i.confirmed).length;
        return (
            <div>
                <div><strong>{ingredients.length}</strong> ingredients, {confirmed} confirmed ({payload.verification || "checked"})</div>
                <div style={{color: "#666"}}>{ingredients.map((i) => i.label_text || i.name).filter(Boolean).join(", ")}</div>
            </div>
        );
    }
    return <div>{JSON.stringify(payload)}</div>;
};

export function ProductErrorReports() {
    const classes = useStyles();
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("new");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        getReports(status);
    }, [status]);

    const getReports = async (statusFilter) => {
        setLoading(true);
        try {
            const res = await axios.get(API_ENDPOINT + '?func=get_product_error_reports&status=' + statusFilter);
            if (res.status === 200 && res.data && !res.data.error) {
                setRows(res.data.reports || []);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (id, newStatus) => {
        try {
            await axios.get(API_ENDPOINT + '?func=resolve_product_error_report&id=' + id + '&status=' + newStatus);
            getReports(status);
        } catch (err) {
            console.log(err);
        }
    };

    const columns = [
        {
            field: 'created',
            headerName: 'Date',
            width: 160,
            renderCell: (cell) => formatDate(cell.row.created),
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 130,
            renderCell: (cell) => {
                const meta = TYPE_LABELS[cell.row.type] || {label: cell.row.type, color: "default"};
                return <Chip size="small" label={meta.label} color={meta.color}/>;
            },
        },
        {
            field: 'product_name',
            headerName: 'Product',
            width: 240,
            renderCell: (cell) => (
                <NavLink to="/products" style={{color: COLOR_BRAND}} state={{id: cell.row.id_product}}>
                    {cell.row.product_name || ('#' + cell.row.id_product)}
                </NavLink>
            ),
        },
        {field: 'product_ean', headerName: 'EAN', width: 140},
        {
            field: 'payload',
            headerName: 'Reported correction',
            flex: 1,
            minWidth: 300,
            sortable: false,
            renderCell: (cell) => <div className={classes.payload}>{renderPayload(cell.row)}</div>,
        },
        {field: 'user_email', headerName: 'Reported by', width: 200},
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (cell) => {
                const color = cell.row.status === 'new' ? 'error' : cell.row.status === 'resolved' ? 'success' : 'default';
                return <Chip size="small" variant="outlined" label={cell.row.status} color={color}/>;
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 200,
            sortable: false,
            renderCell: (cell) => (
                cell.row.status === 'new' ? (
                    <>
                        <Button size="small" color="success" onClick={() => updateStatus(cell.row.id, 'resolved')}>Resolve</Button>
                        <Button size="small" color="inherit" onClick={() => updateStatus(cell.row.id, 'dismissed')}>Dismiss</Button>
                    </>
                ) : (
                    <Button size="small" onClick={() => updateStatus(cell.row.id, 'new')}>Reopen</Button>
                )
            ),
        },
    ];

    return (
        <div className={classes.container}>
            <h1>Error Reports</h1>
            <div className={classes.innerContainer}>
                <div className={classes.box}>
                    <div className={classes.toolbar}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={status}
                            onChange={(e, value) => value && setStatus(value)}
                        >
                            <ToggleButton value="new">New</ToggleButton>
                            <ToggleButton value="all">All</ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                    <DataGrid
                        className={classes.datagrid}
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={loading}
                        pageSize={25}
                        rowsPerPageOptions={[25]}
                        getRowHeight={() => 'auto'}
                        disableColumnMenu
                        autoHeight
                    />
                </div>
            </div>
        </div>
    );
}
